// useSovData.js
// All state, data fetching, and mutation logic for SOVTab3, extracted into
// one hook so the component itself only handles rendering. Every handler
// the JSX needs is returned from here.

import { useState, useEffect, useMemo } from "react";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import {
  getAddressString,
  localDateStr,
  addDaysToDateStr,
  renumberItems,
  getBreakdownAllocation,
  validateBreakdownTotal as validateBreakdownTotalHelper,
  computePreviousValues,
  reconcileItemsWithCOs,
  clampItemsForIntegrity,
  toEmailArray,
  calculateTotalInvoiced as calculateTotalInvoicedHelper,
  isCreditMemoItem,
} from "./SovHelpers";

const BASE_URL = "https://erp-xe9u.onrender.com";

// Legacy CO documents predating the billingMode field only have the old
// `billable` boolean (or nothing at all — schema default is "billable").
// Treat a missing billingMode the same way the backend schema default
// would once the document is re-saved, so old approved COs don't silently
// vanish from the shared draw pool.
const resolveBillingMode = (co) => {
  if (co.billingMode) return co.billingMode;
  return co.billable === false ? "nonBillable" : "billable";
};

export function useSovData(project, setProject) {
  // ── Core SOV form state ──────────────────────────────────────────────
  const [sovData, setSovData] = useState(() => {
    const account = project.accountId || {};
    return {
      billTo: account.companyName || "",
      invoiceDate: "",
      totalInvoiced: "0.00",
      dueDate: "",
      projectName: project.projectName || "",
      job: getAddressString(project.address),
      accountName: account.companyName || "",
      accountAddress: getAddressString(account.address),
      items: [],
      withHoldback: false,
      status: "new",
      generated: false,
      qbInvoiceNumber: null,
      qbInvoiceId: null,
      sentAt: null,
      sendToEmail: project.billingContact?.email || "",
      specialInstructions: "",
      invoiceChangesSeperately: false,
      invoiceComponents: [],
      holdbackInvoiced: false,
      holdbackInvoiceAmount: "0.00",
      poNumber: "",
      drawNumber: 1,
      workCompletedFrom: "",
      workCompletedTo: "",
      sovKind: "progress",
      standaloneChangeOrderId: null,
    };
  });

  // ── Lists ─────────────────────────────────────────────────────────────
  const [sovs, setSovs] = useState([]);
  const [approvedChangeOrders, setApprovedChangeOrders] = useState([]);
  const [allChangeOrders, setAllChangeOrders] = useState([]);
  const [separateChangeOrders, setSeparateChangeOrders] = useState([]);
  const [standaloneSovsByCoId, setStandaloneSovsByCoId] = useState({});
  const [companyProfile, setCompanyProfile] = useState(null);
  const [quickBooksItems, setQuickBooksItems] = useState([]);
  const [quickBooksTaxRates, setQuickBooksTaxRates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [changeOrderPricingSections, setChangeOrderPricingSections] = useState(
    {},
  );

  // ── UI / flow state ───────────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  const [isSendingInvoice, setIsSendingInvoice] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [editingSovId, setEditingSovId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCustomBreakdown, setIsCustomBreakdown] = useState(false);
  const [dueDateTouched, setDueDateTouched] = useState(false);
  const [taxRateData, setTaxRateData] = useState({
    taxRate: "0",
    taxCodeId: "",
  });
  const [showAddCoPicker, setShowAddCoPicker] = useState(false);
  const [customBreakdownItems, setCustomBreakdownItems] = useState([
    { description: "", amount: "0.00" },
  ]);
  const [isBreakdownExpanded, setIsBreakdownExpanded] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [pdfBuffer, setPdfBuffer] = useState(null);
  const [emailForm, setEmailForm] = useState({
    to: "",
    subject: "",
    body: "Dear Client,\n\nPlease find attached the Schedule of Values for your review.\n\nBest regards,",
    signature: "",
  });
  const [invoiceChangesSeperately, setInvoiceChangesSeperately] =
    useState(false);
  const [holdbackInvoiced, setHoldbackInvoiced] = useState(false);
  const [breakdownUnlocked, setBreakdownUnlocked] = useState(false);
  const [confirmUnlockIntent, setConfirmUnlockIntent] = useState(null);
  const [loadingStandaloneId, setLoadingStandaloneId] = useState(null);
  const [standaloneHoldbackStats, setStandaloneHoldbackStats] = useState(null);

  // ── Alert dialog (single shared instance) ────────────────────────────
  const [alert, setAlert] = useState({
    open: false,
    type: "error",
    title: "Error",
    message: "",
  });
  const showAlert = (type, title, message) =>
    setAlert({ open: true, type, title, message });
  const closeAlert = () => setAlert((a) => ({ ...a, open: false }));

  // Keep the "Separate Change Order Invoices" table in sync with edits made
  // inside a standalone invoice form.
  const syncStandaloneCache = (sovId, patch) =>
    setStandaloneSovsByCoId((prev) => {
      const key = Object.keys(prev).find((k) => prev[k]?._id === sovId);
      if (!key) return prev;
      return { ...prev, [key]: { ...prev[key], ...patch } };
    });

  const isStandaloneMode = sovData.sovKind === "co_standalone";

  // ── Contact options ───────────────────────────────────────────────────
  const contactOptions = useMemo(() => {
    const accountId = project?.accountId?._id || project?.accountId;
    return contacts
      .filter((c) => !accountId || c.relatedAccount === accountId)
      .map((c) => ({
        value: c.email,
        label: `${c.firstName} ${c.lastName}${c.email ? ` (${c.email})` : ""}`,
      }));
  }, [contacts, project]);

  const isBreakdownLocked = useMemo(
    () =>
      sovData.items.some(
        (i) =>
          !i.changeOrderNumber &&
          (parseFloat(i.workCompletedPreviously) || 0) > 0,
      ),
    [sovData.items],
  );

  const eligibleCOsToAdd = useMemo(() => {
    const presentCoNumbers = new Set(
      sovData.items
        .filter((i) => i.changeOrderNumber)
        .map((i) => String(i.changeOrderNumber)),
    );
    return approvedChangeOrders.filter(
      (co) => !presentCoNumbers.has(String(co.changeOrderNumber)),
    );
  }, [approvedChangeOrders, sovData.items]);

  const getDefaultQbItemId = () => {
    if (!companyProfile?.plugins?.quickbooks?.connected) return "";
    if (companyProfile?.defaultTax?.defaultItemId)
      return companyProfile.defaultTax.defaultItemId;
    if (quickBooksItems?.length > 0) return quickBooksItems[0].id;
    return "";
  };

  // ── Initial data fetch ────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No authentication token found");

        const projectResponse = await axios.get(
          `${BASE_URL}/api/projects/${project._id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const updatedProject = projectResponse.data;
        setProject(updatedProject);

        const [
          sovResponse,
          companyProfileResponse,
          taxRateResponse,
          contactsResponse,
        ] = await Promise.all([
          fetch(`${BASE_URL}/api/sovs2/project/${project._id}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${BASE_URL}/api/company-profile/`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${BASE_URL}/api/company-profile/tax-rate`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${BASE_URL}/api/contacts`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        setContacts(contactsResponse.data);

        if (!sovResponse.ok) {
          const errorData = await sovResponse.json();
          throw new Error(
            errorData.message || `SOV fetch error: ${sovResponse.status}`,
          );
        }
        const sovJson = await sovResponse.json();
        const isQBConnected =
          taxRateResponse.data.isQuickBooksConnected || false;

        const [qbItemsRes, qbTaxRes] = await Promise.all([
          isQBConnected
            ? axios.get(`${BASE_URL}/api/quickbooks/items`, {
                headers: { Authorization: `Bearer ${token}` },
              })
            : Promise.resolve({ data: [] }),
          isQBConnected
            ? axios.get(`${BASE_URL}/api/quickbooks/tax-codes`, {
                headers: { Authorization: `Bearer ${token}` },
              })
            : Promise.resolve({ data: [] }),
        ]);

        setSovs(sovJson.scheduleOfValues);
        setCompanyProfile({
          ...companyProfileResponse.data,
          defaultTax: {
            taxRate: taxRateResponse.data.taxRate || "0",
            taxCodeId: taxRateResponse.data.taxCodeId || "",
            defaultItemId: taxRateResponse.data.defaultItemId || "",
          },
          plugins: { quickbooks: { connected: isQBConnected } },
        });
        setQuickBooksItems(qbItemsRes.data);
        setQuickBooksTaxRates(qbTaxRes.data);
        setTaxRateData({
          taxRate: taxRateResponse.data.taxRate || "0",
          taxCodeId: taxRateResponse.data.taxCodeId || "",
        });

        let allCOs = [];
        let approvedBillableCOs = [];
        const pricingSectionMap = {};

        try {
          const coResponse = await axios.get(
            `${BASE_URL}/api/changeOrders3/project/${updatedProject._id}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          allCOs = coResponse.data.changeOrders || [];

          approvedBillableCOs = allCOs
            .filter(
              (co) =>
                resolveBillingMode(co) === "billable" &&
                co.status === "Approved",
            )
            .map((co) => ({
              ...co,
              createdAt: co.createdAt || "2025-09-25T00:00:00.000Z",
            }));

          allCOs.forEach((co) => {
            if (co.pricingSections?.length > 0) {
              pricingSectionMap[co.changeOrderNumber] = co.pricingSections;
            }
          });
          setChangeOrderPricingSections(pricingSectionMap);

          const approvedSeparateCOs = allCOs.filter(
            (co) => co.billingMode === "separate" && co.status === "Approved",
          );
          setSeparateChangeOrders(approvedSeparateCOs);

          if (approvedSeparateCOs.length > 0) {
            const standaloneResults = await Promise.all(
              approvedSeparateCOs.map((co) =>
                axios
                  .get(
                    `${BASE_URL}/api/sovs2/change-order/${co._id}/standalone`,
                    {
                      headers: { Authorization: `Bearer ${token}` },
                    },
                  )
                  .then((r) => [co._id, r.data.sov])
                  .catch(() => [co._id, null]),
              ),
            );
            setStandaloneSovsByCoId(Object.fromEntries(standaloneResults));
          } else {
            setStandaloneSovsByCoId({});
          }
        } catch (err) {
          console.error("Failed to load change orders:", err);
        }

        setAllChangeOrders(allCOs);
        setApprovedChangeOrders(approvedBillableCOs);
      } catch (err) {
        showAlert(
          "error",
          "Data Fetch Error",
          `Failed to load data: ${err.message}`,
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project._id]);

  // ── User data fetch ───────────────────────────────────────────────────
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No authentication token found");
        const decoded = jwtDecode(token);
        setUserEmail(decoded.email || "");
        const contactEmail =
          sovData.sendToEmail || project.billingContact?.email || "";
        const clientFirstName = project.billingContact?.firstName || "Client";
        const response = await axios.get(
          `${BASE_URL}/api/users/me/${decoded.email}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const displayName = response.data.success
          ? response.data.user.displayName || "User"
          : "User";
        setEmailForm((prev) => ({
          ...prev,
          to: Array.isArray(sovData.sendToEmail)
            ? sovData.sendToEmail
            : sovData.sendToEmail
              ? [sovData.sendToEmail]
              : contactEmail
                ? [contactEmail]
                : [],
          subject: `${project.projectName} - SOV Invoice#${sovData.qbInvoiceNumber || "Pending"}`,
          body: `Dear ${clientFirstName},\n\nPlease find attached the Schedule of Values for your review.\n\nBest regards,`,
          signature: displayName,
        }));
      } catch {
        // non-critical
      }
    };
    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    project.projectName,
    sovData.sendToEmail,
    sovData.qbInvoiceNumber,
    project.billingContact?.firstName,
  ]);

  // ── Holdback preview for standalone SOVs (read-only) ─────────────────
  useEffect(() => {
    if (!isStandaloneMode || !sovData._id) {
      setStandaloneHoldbackStats(null);
      return;
    }
    const token = localStorage.getItem("token");
    axios
      .get(`${BASE_URL}/api/sovs2/${sovData._id}/holdback-preview`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setStandaloneHoldbackStats(r.data.holdbackStats))
      .catch(() => setStandaloneHoldbackStats(null));
  }, [isStandaloneMode, sovData._id]);

  // ── calculatePreviousInvoiced ─────────────────────────────────────────
  const calculatePreviousInvoiced = (
    description,
    excludeSovId,
    changeOrderNumber = null,
  ) => {
    const sorted = [...sovs]
      .filter((sov) => sov._id !== excludeSovId)
      .sort((a, b) => (b.drawNumber || 0) - (a.drawNumber || 0));
    if (sorted.length === 0) return 0;
    const latestSov = sorted[0];
    if (changeOrderNumber !== null) {
      const match = latestSov.items.find(
        (item) =>
          String(item.changeOrderNumber) === String(changeOrderNumber) &&
          item.description === description,
      );
      return parseFloat(match?.totalCompletedToDate || 0);
    }
    const match = latestSov.items.find(
      (item) => !item.changeOrderNumber && item.description === description,
    );
    return parseFloat(match?.totalCompletedToDate || 0);
  };

  // ── buildFinalItemsWithPrevious ───────────────────────────────────────
  const buildFinalItemsWithPrevious = (breakdownItems, previousItems = []) => {
    const defaultTaxCode = companyProfile?.plugins?.quickbooks?.connected
      ? taxRateData.taxCodeId || quickBooksTaxRates[0]?.id || ""
      : "";
    const defaultTaxRate = companyProfile?.plugins?.quickbooks?.connected
      ? taxRateData.taxRate || "0"
      : parseFloat(taxRateData.taxRate) || 0;

    const rawOriginalItems = breakdownItems.map((bi, index) => {
      const existingItem = previousItems[index];
      return {
        itemNumber: index + 1,
        description: bi.description || `Breakdown Item ${index + 1}`,
        area: bi.area || existingItem?.area || "",
        taxCodeId: existingItem?.taxCodeId || defaultTaxCode,
        taxRate: existingItem?.taxRate || defaultTaxRate.toString(),
        scheduledValue: (parseFloat(bi.amount) || 0).toFixed(2),
        workCompletedPreviously: "0.00",
        workCompletedThisPeriod:
          existingItem?.workCompletedThisPeriod || "0.00",
        totalCompletedToDate: "0.00",
        percentage: "0.00",
        balanceToFinish: (parseFloat(bi.amount) || 0).toFixed(2),
        qbItem: existingItem?.qbItem || getDefaultQbItemId(),
        changeOrderNumber: null,
      };
    });

    const existingCoItems = previousItems
      .filter((i) => !!i.changeOrderNumber)
      .map((item) => ({
        ...item,
        workCompletedPreviously: "0.00",
        totalCompletedToDate: "0.00",
        percentage: "0.00",
        balanceToFinish: item.scheduledValue,
      }));

    const existingCoNumbers = new Set(
      existingCoItems.map((i) => String(i.changeOrderNumber)),
    );

    const newCoItems = approvedChangeOrders
      .filter((co) => !existingCoNumbers.has(String(co.changeOrderNumber)))
      .map((co) => {
        const scheduledValue =
          parseFloat(co.totalCustomerPrice || co.totalPrice) || 0;
        return {
          itemNumber: 0,
          description: co.title,
          area: "",
          changeOrderNumber: String(co.changeOrderNumber),
          changeOrderId: co._id,
          referenceNumber: co.referenceNumber || null,
          taxCodeId: defaultTaxCode,
          taxRate: defaultTaxRate.toString(),
          scheduledValue: scheduledValue.toFixed(2),
          workCompletedPreviously: "0.00",
          workCompletedThisPeriod: "0.00",
          totalCompletedToDate: "0.00",
          percentage: "0.00",
          balanceToFinish: scheduledValue.toFixed(2),
          qbItem: getDefaultQbItemId(),
        };
      });

    const rawItems = renumberItems([
      ...rawOriginalItems,
      ...existingCoItems,
      ...newCoItems,
    ]);
    return computePreviousValues(
      rawItems,
      sovs,
      editingSovId,
      sovData.drawNumber || sovs.length + 1,
      sovData.invoiceDate,
    );
  };

  // ── Field-level handlers ──────────────────────────────────────────────
  const handleSOVChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;
    if (name === "drawNumber")
      finalValue = value === "" ? "" : Number(value) || 1;
    setSovData((prev) => {
      const next = { ...prev, [name]: finalValue };
      if (name === "invoiceDate" && !dueDateTouched && finalValue) {
        next.dueDate = addDaysToDateStr(finalValue, 28);
      }
      return next;
    });
    if (name === "dueDate") setDueDateTouched(true);
  };

  const handlePercentageBlur = (index) => {
    let correctionInfo = null;
    setSovData((prev) => {
      const updatedItems = [...prev.items];
      const item = updatedItems[index];
      if (!item) return prev;

      const scheduledValue = parseFloat(item.scheduledValue) || 0;
      const workCompletedPreviously =
        parseFloat(item.workCompletedPreviously) || 0;
      const currentPercentage = parseFloat(item.percentage) || 0;
      const minPercentage =
        scheduledValue !== 0
          ? Math.min(
              100,
              Math.max(0, (workCompletedPreviously / scheduledValue) * 100),
            )
          : 0;

      if (currentPercentage >= minPercentage - 0.005) return prev;

      const cappedPercentage = minPercentage;
      const totalCompleted = (cappedPercentage / 100) * scheduledValue;
      const workCompletedThisPeriod = totalCompleted - workCompletedPreviously;
      const correctedTotalCompleted =
        workCompletedPreviously + workCompletedThisPeriod;

      updatedItems[index] = {
        ...item,
        percentage: cappedPercentage.toFixed(2),
        workCompletedThisPeriod: workCompletedThisPeriod.toFixed(2),
        totalCompletedToDate: correctedTotalCompleted.toFixed(2),
        balanceToFinish: (scheduledValue - correctedTotalCompleted).toFixed(2),
      };
      correctionInfo = {
        description: item.description || `Item ${index + 1}`,
        attempted: currentPercentage.toFixed(2),
        corrected: cappedPercentage.toFixed(2),
      };
      return { ...prev, items: updatedItems };
    });

    if (correctionInfo) {
      showAlert(
        "warning",
        "Percentage Adjusted",
        `"${correctionInfo.description}" can't go below ${correctionInfo.corrected}% complete because that much has already been billed in a previous draw. It was adjusted from ${correctionInfo.attempted}% back up to ${correctionInfo.corrected}%.`,
      );
    }
  };

  const handleItemChange = (index, field, value) => {
    setSovData((prev) => {
      const updatedItems = [...prev.items];
      if (
        isCustomBreakdown &&
        !updatedItems[index].changeOrderNumber &&
        field === "description"
      ) {
        return prev;
      }
      let formattedValue = value;

      if (field === "percentage") {
        const rawNum = typeof value === "number" ? value : parseFloat(value);
        if (!Number.isFinite(rawNum)) return prev;
        const scheduledValue =
          parseFloat(updatedItems[index].scheduledValue) || 0;
        const workCompletedPreviously =
          parseFloat(updatedItems[index].workCompletedPreviously) || 0;
        const totalCompleted = (rawNum / 100) * scheduledValue;
        const workCompletedThisPeriod =
          totalCompleted - workCompletedPreviously;
        const correctedTotalCompleted =
          workCompletedPreviously + workCompletedThisPeriod;

        updatedItems[index] = {
          ...updatedItems[index],
          percentage: rawNum.toFixed(2),
          workCompletedThisPeriod: workCompletedThisPeriod.toFixed(2),
          totalCompletedToDate: correctedTotalCompleted.toFixed(2),
          balanceToFinish: (scheduledValue - correctedTotalCompleted).toFixed(
            2,
          ),
        };
        return { ...prev, items: updatedItems };
      }

      if (["scheduledValue", "taxRate"].includes(field)) {
        formattedValue = (parseFloat(value) || 0).toFixed(2);
      }

      updatedItems[index] = { ...updatedItems[index], [field]: formattedValue };

      if (field === "scheduledValue") {
        const workPrev =
          parseFloat(updatedItems[index].workCompletedPreviously) || 0;
        let newSV = parseFloat(formattedValue) || 0;
        const prevSign = Math.sign(workPrev);
        const newSign = Math.sign(newSV);
        const magnitudeTooSmall = Math.abs(newSV) < Math.abs(workPrev);
        const signFlipped =
          prevSign !== 0 && newSign !== 0 && prevSign !== newSign;

        if (workPrev !== 0 && (magnitudeTooSmall || signFlipped)) {
          newSV = workPrev;
          formattedValue = newSV.toFixed(2);
          updatedItems[index].scheduledValue = formattedValue;
          const rowDescription =
            updatedItems[index].description || `Item ${index + 1}`;
          setTimeout(() => {
            showAlert(
              "warning",
              "Scheduled Value Adjusted",
              `"${rowDescription}" can't have a Scheduled Value below what's already been billed (${workPrev.toFixed(2)}) in a previous draw. It was kept at ${newSV.toFixed(2)}.`,
            );
          }, 0);
        }

        const workThis =
          parseFloat(updatedItems[index].workCompletedThisPeriod) || 0;
        const totalCompleted = workPrev + workThis;
        updatedItems[index].totalCompletedToDate = totalCompleted.toFixed(2);
        updatedItems[index].percentage = newSV
          ? ((totalCompleted / newSV) * 100).toFixed(2)
          : "0.00";
        updatedItems[index].balanceToFinish = (newSV - totalCompleted).toFixed(
          2,
        );
      }

      return { ...prev, items: updatedItems };
    });
  };

  const removeLineItem = (index) => {
    setSovData((prev) => {
      if (prev.status === "voided") return prev;
      const item = prev.items[index];
      const hasProgress =
        (parseFloat(item?.workCompletedPreviously) || 0) !== 0 ||
        (parseFloat(item?.workCompletedThisPeriod) || 0) !== 0;
      if (hasProgress) {
        showAlert(
          "error",
          "Cannot Remove Item",
          `"${item.description}" has recorded work (previous or current period) and cannot be removed.`,
        );
        return prev;
      }
      const updatedItems = prev.items
        .filter((_, i) => i !== index)
        .map((it, i) => ({ ...it, itemNumber: i + 1 }));
      return { ...prev, items: updatedItems };
    });
  };

  const moveItemUp = (index) => {
    if (index === 0 || sovData.status === "voided") return;
    setSovData((prev) => {
      const updatedItems = [...prev.items];
      [updatedItems[index - 1], updatedItems[index]] = [
        updatedItems[index],
        updatedItems[index - 1],
      ];
      updatedItems.forEach((item, i) => (item.itemNumber = i + 1));
      return { ...prev, items: updatedItems };
    });
  };

  const moveItemDown = (index) => {
    if (index === sovData.items.length - 1 || sovData.status === "voided")
      return;
    setSovData((prev) => {
      const updatedItems = [...prev.items];
      [updatedItems[index], updatedItems[index + 1]] = [
        updatedItems[index + 1],
        updatedItems[index],
      ];
      updatedItems.forEach((item, i) => (item.itemNumber = i + 1));
      return { ...prev, items: updatedItems };
    });
  };

  // ── Custom breakdown handlers ─────────────────────────────────────────
  const breakdownItemHasProgress = (index) => {
    const bi = customBreakdownItems[index];
    if (!bi) return false;
    const description = bi.description || `Breakdown Item ${index + 1}`;
    const sovItem = sovData.items.find(
      (si) => !si.changeOrderNumber && si.description === description,
    );
    if (!sovItem) return false;
    return (
      (parseFloat(sovItem.workCompletedPreviously) || 0) > 0 ||
      (parseFloat(sovItem.workCompletedThisPeriod) || 0) > 0
    );
  };

  const handleBreakdownItemChange = (index, field, value) => {
    if (
      isBreakdownLocked &&
      !breakdownUnlocked &&
      breakdownItemHasProgress(index)
    )
      return;

    setCustomBreakdownItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });

    setSovData((prev) => {
      const updatedItems = prev.items.map((item, i) => {
        if (i === index && !item.changeOrderNumber) {
          if (field === "description" || field === "area") {
            return {
              ...item,
              [field]:
                value ||
                (field === "description" ? `Breakdown Item ${index + 1}` : ""),
            };
          }
          if (field === "amount") {
            const scheduledValue = (parseFloat(value) || 0).toFixed(2);
            const totalCompleted =
              (parseFloat(item.workCompletedPreviously) || 0) +
              (parseFloat(item.workCompletedThisPeriod) || 0);
            return {
              ...item,
              scheduledValue,
              totalCompletedToDate: totalCompleted.toFixed(2),
              percentage: parseFloat(scheduledValue)
                ? ((totalCompleted / parseFloat(scheduledValue)) * 100).toFixed(
                    2,
                  )
                : "0.00",
              balanceToFinish: (
                parseFloat(scheduledValue) - totalCompleted
              ).toFixed(2),
            };
          }
        }
        return item;
      });

      if (editingSovId) {
        const originalItems = updatedItems.filter(
          (item) => !item.changeOrderNumber,
        );
        const coItems = updatedItems.filter((item) => item.changeOrderNumber);
        return {
          ...prev,
          items: [...originalItems, ...coItems].map((item, idx) => ({
            ...item,
            itemNumber: idx + 1,
          })),
        };
      }

      return {
        ...prev,
        items: buildFinalItemsWithPrevious(
          updatedItems
            .filter((_, i) => i < customBreakdownItems.length)
            .map((item, i) => ({
              description:
                i === index && field === "description"
                  ? value
                  : item.description,
              amount:
                i === index && field === "amount" ? value : item.scheduledValue,
              area: i === index && field === "area" ? value : item.area || "",
            })),
          updatedItems,
        ),
      };
    });
  };

  useEffect(() => {
    if (!isCustomBreakdown || editingSovId) return;
    const currentOriginalItems = sovData.items.filter(
      (i) => !i.changeOrderNumber,
    );
    if (currentOriginalItems.length === customBreakdownItems.length) {
      const allMatch = customBreakdownItems.every((bi, idx) => {
        const si = currentOriginalItems[idx];
        if (!si) return false;
        return (
          si.description === (bi.description || `Breakdown Item ${idx + 1}`) &&
          si.scheduledValue === (parseFloat(bi.amount) || 0).toFixed(2)
        );
      });
      if (allMatch) return;
    }
    const rebuilt = buildFinalItemsWithPrevious(
      customBreakdownItems,
      sovData.items,
    );
    setSovData((prev) => {
      if (JSON.stringify(prev.items) === JSON.stringify(rebuilt)) return prev;
      return { ...prev, items: rebuilt };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customBreakdownItems, isCustomBreakdown, editingSovId]);

  const disableCustomBreakdown = () => {
    setIsCustomBreakdown(false);
    setCustomBreakdownItems([{ description: "", amount: "0.00" }]);
    const sv = parseFloat(project.originalContractValue) || 0;
    const prevInvoiced = calculatePreviousInvoiced(
      "Original Contract Value",
      editingSovId,
    );
    const workThis = editingSovId
      ? sovData.items.find((i) => i.description === "Original Contract Value")
          ?.workCompletedThisPeriod || "0.00"
      : "0.00";
    const totalCompleted = (prevInvoiced + parseFloat(workThis)).toFixed(2);
    const originalItem = {
      itemNumber: 1,
      description: "Original Contract Value",
      taxCodeId: taxRateData.taxCodeId || "",
      taxRate: taxRateData.taxRate || "0",
      scheduledValue: sv.toFixed(2),
      workCompletedPreviously: prevInvoiced.toFixed(2),
      workCompletedThisPeriod: workThis,
      totalCompletedToDate: totalCompleted,
      percentage: sv
        ? ((parseFloat(totalCompleted) / sv) * 100).toFixed(2)
        : "0.00",
      balanceToFinish: (sv - parseFloat(totalCompleted)).toFixed(2),
      qbItem: getDefaultQbItemId(),
      changeOrderNumber: null,
    };
    const coItems = sovData.items
      .filter((i) => i.changeOrderNumber)
      .map((i, idx) => ({ ...i, itemNumber: 2 + idx }));
    setSovData((prev) => ({ ...prev, items: [originalItem, ...coItems] }));
    setIsBreakdownExpanded(false);
    setBreakdownUnlocked(false);
  };

  const addBreakdownItem = () => {
    setCustomBreakdownItems((prev) => [
      ...prev,
      { description: "", amount: "0.00", area: "" },
    ]);
    setSovData((prev) => {
      const defaultTaxCode = companyProfile?.plugins?.quickbooks?.connected
        ? taxRateData.taxCodeId || quickBooksTaxRates[0]?.id || ""
        : "";
      const defaultTaxRate = companyProfile?.plugins?.quickbooks?.connected
        ? ""
        : parseFloat(companyProfile?.defaultTaxRate) || 0;
      const newItem = {
        itemNumber:
          prev.items.filter((item) => !item.changeOrderNumber).length + 1,
        description: `Breakdown Item ${prev.items.length + 1}`,
        taxCodeId: defaultTaxCode,
        taxRate: defaultTaxRate.toString(),
        scheduledValue: "0.00",
        workCompletedPreviously: "0.00",
        workCompletedThisPeriod: "0.00",
        totalCompletedToDate: "0.00",
        percentage: "0.00",
        balanceToFinish: "0.00",
        qbItem: getDefaultQbItemId(),
      };
      const updatedItems = prev.items.map((item, index) => ({
        ...item,
        itemNumber: index + 1,
      }));
      return {
        ...prev,
        items: [
          ...updatedItems.filter((item) => !item.changeOrderNumber),
          newItem,
          ...updatedItems.filter((item) => item.changeOrderNumber),
        ],
      };
    });
  };

  const removeBreakdownItem = (index) => {
    setCustomBreakdownItems((prev) => {
      const removedItem = prev[index];
      const description =
        removedItem.description || `Breakdown Item ${index + 1}`;
      const sovItem = sovData.items.find(
        (item) => item.description === description,
      );
      if (sovItem) {
        const hasProgress =
          (parseFloat(sovItem.workCompletedPreviously) || 0) > 0 ||
          (parseFloat(sovItem.workCompletedThisPeriod) || 0) > 0;
        if (hasProgress) {
          showAlert(
            "error",
            "Cannot Remove Item",
            `Cannot remove "${description}" because it has recorded work.`,
          );
          return prev;
        }
      }
      if (prev.length === 1) {
        showAlert(
          "error",
          "Cannot Remove Item",
          "At least one breakdown item is required.",
        );
        return prev;
      }
      const updated = prev.filter((_, i) => i !== index);
      setSovData((prevSov) => ({
        ...prevSov,
        items: prevSov.items
          .filter((item) => item.description !== description)
          .map((item, i) => ({ ...item, itemNumber: i + 1 })),
      }));
      return updated;
    });
  };

  // ── Add existing approved CO to the current draw ─────────────────────
  const addChangeOrderToDraw = (co) => {
    const alreadyPresent = sovData.items.some(
      (i) => String(i.changeOrderNumber) === String(co.changeOrderNumber),
    );
    if (alreadyPresent) {
      showAlert(
        "error",
        "Already Added",
        `CO #${co.changeOrderNumber} is already on this draw.`,
      );
      return;
    }

    const hasExistingInvoices =
      sovData.invoiceComponents?.length > 0 &&
      sovData.invoiceComponents.some(
        (ic) => ic.qbInvoiceId && ic.status !== "voided",
      );

    const defaultTaxCode = companyProfile?.plugins?.quickbooks?.connected
      ? taxRateData.taxCodeId || quickBooksTaxRates[0]?.id || ""
      : "";
    const defaultTaxRate = companyProfile?.plugins?.quickbooks?.connected
      ? taxRateData.taxRate || "0"
      : parseFloat(taxRateData.taxRate) || 0;

    const scheduledValue =
      parseFloat(co.totalCustomerPrice || co.totalPrice) || 0;
    const prevInvoiced = calculatePreviousInvoiced(
      co.title,
      editingSovId,
      co.changeOrderNumber,
    );

    const newItem = {
      itemNumber: 0,
      description: co.title,
      area: "",
      changeOrderNumber: String(co.changeOrderNumber),
      changeOrderId: co._id,
      referenceNumber: co.referenceNumber || null,
      taxCodeId: defaultTaxCode,
      taxRate: defaultTaxRate.toString(),
      scheduledValue: scheduledValue.toFixed(2),
      workCompletedPreviously: prevInvoiced.toFixed(2),
      workCompletedThisPeriod: "0.00",
      totalCompletedToDate: prevInvoiced.toFixed(2),
      percentage: scheduledValue
        ? ((prevInvoiced / scheduledValue) * 100).toFixed(2)
        : "0.00",
      balanceToFinish: (scheduledValue - prevInvoiced).toFixed(2),
      qbItem: getDefaultQbItemId(),
      _addedAfterInvoice: hasExistingInvoices,
    };

    setSovData((prev) => ({
      ...prev,
      items: renumberItems([...prev.items, newItem]),
    }));
    setShowAddCoPicker(false);

    if (hasExistingInvoices) {
      showAlert(
        "warning",
        "Change Order Added — Regeneration Needed",
        `CO #${co.changeOrderNumber} ("${co.title}") was added to this draw, but QuickBooks invoices were already generated before this CO existed. It will NOT be included in the client's invoice until you regenerate QB invoices for this draw.`,
      );
    }
  };

  // ── View existing SOV (normal draw OR standalone) ────────────────────
  const handleViewSOV = (sov) => {
    const originalContractItems = sov.items.filter(
      (item) => !item.changeOrderNumber,
    );
    const isSovCustomBreakdown =
      originalContractItems.length > 1 || sov.isCustomBreakdown;
    setIsCustomBreakdown(isSovCustomBreakdown);
    setCustomBreakdownItems(
      isSovCustomBreakdown
        ? originalContractItems.map((item) => ({
            description: item.description,
            amount: item.scheduledValue,
            area: item.area || "",
          }))
        : [{ description: "", amount: "0.00" }],
    );
    setIsBreakdownExpanded(false);
    setInvoiceChangesSeperately(sov.invoiceChangesSeperately || false);
    setHoldbackInvoiced(sov.holdbackInvoiced || false);
    setBreakdownUnlocked(false);
    setDueDateTouched(true);

    const withPrev = sov.items.map((item, index) => ({
      ...item,
      itemNumber: index + 1,
      workCompletedPreviously: (
        parseFloat(item.workCompletedPreviously) || 0
      ).toFixed(2),
      workCompletedThisPeriod: (
        parseFloat(item.workCompletedThisPeriod) || 0
      ).toFixed(2),
      totalCompletedToDate: (
        parseFloat(item.totalCompletedToDate) || 0
      ).toFixed(2),
      balanceToFinish: (
        (parseFloat(item.scheduledValue) || 0) -
        (parseFloat(item.totalCompletedToDate) || 0)
      ).toFixed(2),
      percentage: parseFloat(item.scheduledValue)
        ? (
            ((parseFloat(item.totalCompletedToDate) || 0) /
              Math.abs(parseFloat(item.scheduledValue))) *
            100
          ).toFixed(2)
        : "0.00",
    }));

    const reconciledItems =
      sov.status === "new" && sov.sovKind !== "co_standalone"
        ? reconcileItemsWithCOs(withPrev, allChangeOrders)
        : withPrev;

    setSovData({
      ...sov,
      items: reconciledItems,
      accountName: sov.accountName || project.accountId?.companyName || "N/A",
      accountAddress:
        sov.accountAddress ||
        getAddressString(project.accountId?.address || {}),
      projectName: sov.projectName || project.projectName || "N/A",
      sendToEmail: Array.isArray(sov.sendToEmail)
        ? sov.sendToEmail
        : sov.sendToEmail
          ? [sov.sendToEmail]
          : project.billingContact?.email
            ? [project.billingContact.email]
            : [],
      specialInstructions: sov.specialInstructions || "",
      poNumber: sov.poNumber || "",
      drawNumber: sov.drawNumber || 1,
      workCompletedFrom: sov.workCompletedFrom || "",
      workCompletedTo: sov.workCompletedTo || "",
      sovKind: sov.sovKind || "progress",
      standaloneChangeOrderId: sov.standaloneChangeOrderId || null,
    });
    setEditingSovId(sov._id);
    setShowCreateForm(true);
  };

  // ── Create standalone SOV for a separate CO, then open it ────────────
  const handleOpenStandaloneSov = async (co) => {
    setLoadingStandaloneId(co._id);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${BASE_URL}/api/sovs2/change-order/${co._id}/standalone`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const sov = res.data.sov;
      setStandaloneSovsByCoId((prev) => ({ ...prev, [co._id]: sov }));
      handleViewSOV(sov);
    } catch (err) {
      showAlert(
        "error",
        "Standalone Invoice Error",
        `Failed to open standalone invoice: ${err.response?.data?.message || err.message}`,
      );
    } finally {
      setLoadingStandaloneId(null);
    }
  };

  const prepareHoldbackReleaseForCurrentSov = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${BASE_URL}/api/sovs2/${sovData._id}/prepare-holdback-release`,
        { releaseAll: true },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSovData((prev) => ({
        ...prev,
        holdbackInvoiced: true,
        holdbackInvoiceAmount: res.data.holdbackAmount,
      }));
      showAlert("success", "Holdback Release Prepared", res.data.message);
    } catch (err) {
      showAlert(
        "error",
        "Holdback Release Error",
        err.response?.data?.message || err.message,
      );
    }
  };

  // ── Create new SOV (normal draw) ──────────────────────────────────────
  const handleCreateSOV = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token");
      setEditingSovId(null);

      const projectResponse = await axios.get(
        `${BASE_URL}/api/projects/${project._id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const updatedProject = projectResponse.data;
      setProject(updatedProject);

      let nextDrawNumber = 1;
      try {
        const drawRes = await axios.get(
          `${BASE_URL}/api/sovs2/project/${project._id}/next-draw-number`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        nextDrawNumber = drawRes.data.nextDrawNumber || 1;
      } catch {
        nextDrawNumber = sovs.length + 1;
      }

      let freshApprovedCOs = [];
      let freshAllCOs = [];
      try {
        const coResponse = await axios.get(
          `${BASE_URL}/api/changeOrders3/project/${updatedProject._id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        freshAllCOs = coResponse.data.changeOrders || [];
        freshApprovedCOs = freshAllCOs
          .filter(
            (co) =>
              resolveBillingMode(co) === "billable" && co.status === "Approved",
          )
          .map((co) => ({
            ...co,
            createdAt: co.createdAt || "2025-09-25T00:00:00.000Z",
          }));

        const pricingSectionMap = {};
        freshAllCOs.forEach((co) => {
          if (co.pricingSections?.length > 0)
            pricingSectionMap[co.changeOrderNumber] = co.pricingSections;
        });
        setChangeOrderPricingSections(pricingSectionMap);
      } catch (err) {
        console.error("Failed to load change orders:", err);
      }

      setAllChangeOrders(freshAllCOs);
      setApprovedChangeOrders(freshApprovedCOs);

      const sortedSovs = [...sovs].sort((a, b) => {
        const drawDiff = (a.drawNumber || 0) - (b.drawNumber || 0);
        if (drawDiff !== 0) return drawDiff;
        return (a.createdAt || "").localeCompare(b.createdAt || "");
      });
      const latestSov =
        sortedSovs.length > 0 ? sortedSovs[sortedSovs.length - 1] : null;
      const latestOriginalItems = latestSov
        ? latestSov.items.filter((i) => !i.changeOrderNumber)
        : [];
      const latestCoItems = latestSov
        ? latestSov.items.filter((i) => i.changeOrderNumber)
        : [];

      let useCustomBreakdown = false;
      let breakdownForNew = [{ description: "", amount: "0.00", area: "" }];
      if (latestSov) {
        useCustomBreakdown =
          latestOriginalItems.length > 1 || !!latestSov.isCustomBreakdown;
        if (useCustomBreakdown) {
          breakdownForNew = latestOriginalItems.map((i) => ({
            description: i.description,
            amount: i.scheduledValue,
            area: i.area || "",
          }));
        }
      }

      const useInvoiceChangesSeperately =
        latestSov?.invoiceChangesSeperately || false;
      setIsCustomBreakdown(useCustomBreakdown);
      setCustomBreakdownItems(breakdownForNew);
      setInvoiceChangesSeperately(useInvoiceChangesSeperately);
      setHoldbackInvoiced(false);

      const defaultTaxCode = companyProfile?.plugins?.quickbooks?.connected
        ? taxRateData.taxCodeId || quickBooksTaxRates[0]?.id || ""
        : "";
      const defaultTaxRate = companyProfile?.plugins?.quickbooks?.connected
        ? taxRateData.taxRate || "0"
        : parseFloat(taxRateData.taxRate) || 0;

      let rawOriginalItems = [];
      if (useCustomBreakdown && breakdownForNew.length > 0) {
        rawOriginalItems = breakdownForNew.map((item, index) => ({
          itemNumber: index + 1,
          description: item.description || `Breakdown Item ${index + 1}`,
          area: item.area || "",
          taxCodeId: defaultTaxCode,
          taxRate: defaultTaxRate.toString(),
          scheduledValue: (parseFloat(item.amount) || 0).toFixed(2),
          workCompletedPreviously: "0.00",
          workCompletedThisPeriod: "0.00",
          totalCompletedToDate: "0.00",
          percentage: "0.00",
          balanceToFinish: (parseFloat(item.amount) || 0).toFixed(2),
          qbItem: getDefaultQbItemId(),
          changeOrderNumber: null,
        }));
      } else {
        const sv = parseFloat(updatedProject.originalContractValue) || 0;
        rawOriginalItems = [
          {
            itemNumber: 1,
            description: "Original Contract Value",
            area: "",
            taxCodeId: defaultTaxCode,
            taxRate: defaultTaxRate.toString(),
            scheduledValue: sv.toFixed(2),
            workCompletedPreviously: "0.00",
            workCompletedThisPeriod: "0.00",
            totalCompletedToDate: "0.00",
            percentage: "0.00",
            balanceToFinish: sv.toFixed(2),
            qbItem: getDefaultQbItemId(),
            changeOrderNumber: null,
          },
        ];
      }

      const existingCoMap = new Map();
      latestCoItems.forEach((item) =>
        existingCoMap.set(String(item.changeOrderNumber), item),
      );
      const approvedCoNumbers = new Set(
        freshApprovedCOs.map((co) => String(co.changeOrderNumber)),
      );

      const rawExistingCoItems = Array.from(existingCoMap.values())
        .filter((item) => approvedCoNumbers.has(String(item.changeOrderNumber)))
        .map((item) => {
          const liveCo = freshApprovedCOs.find(
            (co) =>
              String(co.changeOrderNumber) === String(item.changeOrderNumber),
          );
          const scheduledValue = liveCo
            ? parseFloat(liveCo.totalCustomerPrice || 0)
            : parseFloat(item.scheduledValue) || 0;
          return {
            itemNumber: 0,
            description: item.description,
            area: "",
            changeOrderNumber: String(item.changeOrderNumber),
            changeOrderId: item.changeOrderId,
            referenceNumber:
              item.referenceNumber || liveCo?.referenceNumber || null,
            taxCodeId: item.taxCodeId || defaultTaxCode,
            taxRate: item.taxRate || defaultTaxRate.toString(),
            scheduledValue: scheduledValue.toFixed(2),
            workCompletedPreviously: "0.00",
            workCompletedThisPeriod: "0.00",
            totalCompletedToDate: "0.00",
            percentage: "0.00",
            balanceToFinish: scheduledValue.toFixed(2),
            qbItem: getDefaultQbItemId(),
          };
        });

      const rawNewCoItems = freshApprovedCOs
        .filter((co) => !existingCoMap.has(String(co.changeOrderNumber)))
        .map((co) => {
          const sv = parseFloat(co.totalCustomerPrice || co.totalPrice) || 0;
          return {
            itemNumber: 0,
            description: co.title,
            area: "",
            changeOrderNumber: String(co.changeOrderNumber),
            changeOrderId: co._id,
            referenceNumber: co.referenceNumber || null,
            taxCodeId: defaultTaxCode,
            taxRate: defaultTaxRate.toString(),
            scheduledValue: sv.toFixed(2),
            workCompletedPreviously: "0.00",
            workCompletedThisPeriod: "0.00",
            totalCompletedToDate: "0.00",
            percentage: "0.00",
            balanceToFinish: sv.toFixed(2),
            qbItem: getDefaultQbItemId(),
          };
        });

      const rawItems = renumberItems([
        ...rawOriginalItems,
        ...rawExistingCoItems,
        ...rawNewCoItems,
      ]);
      const defaultInvoiceDate = localDateStr();
      const defaultDueDate = addDaysToDateStr(defaultInvoiceDate, 28);
      setDueDateTouched(false);
      setBreakdownUnlocked(false);

      const finalItems = computePreviousValues(
        rawItems,
        sortedSovs,
        null,
        nextDrawNumber,
        defaultInvoiceDate,
      );

      setSovData((prev) => ({
        ...prev,
        billTo: updatedProject.accountId?.companyName || "",
        projectName: updatedProject.projectName || "",
        job: getAddressString(updatedProject.address),
        accountName: updatedProject.accountId?.companyName || "",
        accountAddress: getAddressString(
          updatedProject.accountId?.address || {},
        ),
        sendToEmail: updatedProject.billingContact?.email
          ? [updatedProject.billingContact.email]
          : [],
        withHoldback: latestSov?.withHoldback ?? false,
        specialInstructions: latestSov?.specialInstructions || "",
        items: finalItems,
        qbInvoiceNumber: null,
        qbInvoiceId: null,
        generated: false,
        status: "new",
        sentAt: null,
        invoiceDate: defaultInvoiceDate,
        dueDate: defaultDueDate,
        totalInvoiced: "0.00",
        invoiceChangesSeperately: useInvoiceChangesSeperately,
        invoiceComponents: [],
        holdbackInvoiced: false,
        holdbackInvoiceAmount: "0.00",
        poNumber: latestSov?.poNumber || "",
        drawNumber: nextDrawNumber,
        workCompletedFrom: "",
        workCompletedTo: "",
        sovKind: "progress",
        standaloneChangeOrderId: null,
      }));

      setIsBreakdownExpanded(false);
      setShowCreateForm(true);
    } catch (err) {
      console.error("Create SOV failed:", err);
      showAlert(
        "error",
        "Create SOV Error",
        `Failed to create SOV: ${err.message}`,
      );
    }
  };

  // ── Holdback SOV (release invoice) ────────────────────────────────────
  const calculateTotalHoldbackDeducted = () => {
    let total = 0;
    sovs.forEach((sov) => {
      if (sov._id === sovData._id) return;
      sov.invoiceComponents?.forEach((comp) => {
        if (comp.component !== "holdback" && comp.holdback)
          total += parseFloat(comp.holdback) || 0;
      });
    });
    return total;
  };

  const calculateTotalHoldbackReleased = () => {
    let total = 0;
    sovs.forEach((sov) => {
      sov.invoiceComponents?.forEach((comp) => {
        if (comp.component === "holdback")
          total += parseFloat(comp.amount || comp.total || "0");
      });
      if (
        sov.holdbackInvoiced &&
        parseFloat(sov.holdbackInvoiceAmount || "0") > 0
      ) {
        const hasHoldbackComponent = sov.invoiceComponents?.some(
          (comp) => comp.component === "holdback",
        );
        if (!hasHoldbackComponent)
          total += parseFloat(sov.holdbackInvoiceAmount || "0");
      }
    });
    return total;
  };

  const calculateOutstandingHoldback = () =>
    Math.max(
      0,
      calculateTotalHoldbackDeducted() - calculateTotalHoldbackReleased(),
    );

  const handleInvoiceHoldbackSOV = async () => {
    try {
      const token = localStorage.getItem("token");
      const projectResponse = await axios.get(
        `${BASE_URL}/api/projects/${project._id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setProject(projectResponse.data);

      let nextDrawNumber = 1;
      try {
        const drawRes = await axios.get(
          `${BASE_URL}/api/sovs2/project/${project._id}/next-draw-number`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        nextDrawNumber = drawRes.data.nextDrawNumber || 1;
      } catch {
        nextDrawNumber = sovs.length + 1;
      }

      const outstanding = calculateOutstandingHoldback();
      if (outstanding <= 0) return;

      const holdbackItem = {
        itemNumber: 1,
        description: "Release of Retained Holdback",
        area: "",
        changeOrderNumber: null,
        taxCodeId: companyProfile?.defaultTax?.taxCodeId || "NON",
        taxRate: companyProfile?.defaultTax?.taxRate?.toString() || "0",
        scheduledValue: outstanding.toFixed(2),
        workCompletedPreviously: "0.00",
        workCompletedThisPeriod: outstanding.toFixed(2),
        totalCompletedToDate: outstanding.toFixed(2),
        percentage: "100.00",
        balanceToFinish: "0.00",
        qbItem:
          companyProfile?.defaultTax?.defaultItemId || getDefaultQbItemId(),
      };

      const defaultInvoiceDate = localDateStr();
      setDueDateTouched(false);

      setSovData({
        ...sovData,
        projectName: project.projectName || "",
        job: getAddressString(project.address),
        accountName: project.accountId?.companyName || "",
        accountAddress: getAddressString(project.accountId?.address || {}),
        sendToEmail: project.billingContact?.email
          ? [project.billingContact.email]
          : [],
        withHoldback: false,
        poNumber: sovs[0]?.poNumber || "",
        drawNumber: nextDrawNumber,
        items: [holdbackItem],
        holdbackInvoiced: true,
        holdbackInvoiceAmount: outstanding.toFixed(2),
        status: "new",
        generated: false,
        invoiceDate: defaultInvoiceDate,
        dueDate: addDaysToDateStr(defaultInvoiceDate, 28),
        invoiceComponents: [],
        sovKind: "progress",
        standaloneChangeOrderId: null,
      });

      setEditingSovId(null);
      setShowCreateForm(true);
    } catch (err) {
      showAlert(
        "error",
        "Error",
        "Failed to prepare holdback invoice: " + err.message,
      );
    }
  };

  // ── Save SOV ───────────────────────────────────────────────────────────
  const saveSOV = async (dataToSubmit) => {
    const token = localStorage.getItem("token");
    if (!token) {
      showAlert(
        "error",
        "Authentication Error",
        "Please log in to submit SOV.",
      );
      return null;
    }
    try {
      const payload = {
        billTo: dataToSubmit.billTo,
        invoiceDate: dataToSubmit.invoiceDate,
        dueDate: dataToSubmit.dueDate,
        job: dataToSubmit.job,
        items: dataToSubmit.items ?? [],
        withHoldback: !!dataToSubmit.withHoldback,
        status: dataToSubmit.status || "new",
        generated: !!dataToSubmit.generated,
        totalInvoiced: dataToSubmit.totalInvoiced || "0.00",
        isCustomBreakdown: !!dataToSubmit.isCustomBreakdown,
        sendToEmail: toEmailArray(dataToSubmit.sendToEmail).join(","),
        specialInstructions: dataToSubmit.specialInstructions || "",
        invoiceChangesSeperately: !!dataToSubmit.invoiceChangesSeperately,
        invoiceComponents: dataToSubmit.invoiceComponents ?? [],
        holdbackInvoiced: !!dataToSubmit.holdbackInvoiced,
        holdbackInvoiceAmount: dataToSubmit.holdbackInvoiceAmount || "0.00",
        poNumber: dataToSubmit.poNumber || "",
        drawNumber: Number(dataToSubmit.drawNumber) || 1,
        workCompletedFrom: dataToSubmit.workCompletedFrom || "",
        workCompletedTo: dataToSubmit.workCompletedTo || "",
      };

      const method = editingSovId ? "PUT" : "POST";
      const url = editingSovId
        ? `${BASE_URL}/api/sovs2/project/${project._id}/${editingSovId}`
        : `${BASE_URL}/api/sovs2/project/${project._id}`;

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`,
        );
      }

      const data = await response.json();
      setSovs((prev) =>
        editingSovId
          ? prev.map((sov) => (sov._id === data._id ? data : sov))
          : [...prev, data],
      );

      // Built from what we submitted + what the server returned, so the
      // caller gets the fresh state immediately (React state won't have
      // updated yet when the caller continues).
      const merged = {
        ...dataToSubmit,
        ...data,
        items: renumberItems(data.items || []),
        sendToEmail: toEmailArray(data.sendToEmail),
        invoiceComponents:
          data.invoiceComponents ?? dataToSubmit.invoiceComponents ?? [],
        sovKind: data.sovKind || dataToSubmit.sovKind || "progress",
        standaloneChangeOrderId:
          data.standaloneChangeOrderId ??
          dataToSubmit.standaloneChangeOrderId ??
          null,
      };

      setSovData((prev) => ({ ...prev, ...merged }));
      setEditingSovId(data._id);
      if (data.sovKind === "co_standalone") syncStandaloneCache(data._id, data);

      showAlert(
        "success",
        "Success",
        editingSovId
          ? "SOV updated successfully."
          : "SOV created successfully.",
      );
      return merged;
    } catch (err) {
      showAlert(
        "error",
        "Save SOV Error",
        `Failed to save SOV: ${err.message}`,
      );
      return null;
    }
  };

  // ── Submit handler ─────────────────────────────────────────────────────
  // Returns { sovData, regenerated } so the component can download the
  // fresh invoices right after — or null if nothing was saved.
  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (isSubmitting) return null;

    if (isCustomBreakdown) {
      const breakdownError = validateBreakdownTotalHelper(
        customBreakdownItems,
        project.originalContractValue,
      );
      if (breakdownError) {
        showAlert("error", "Breakdown Validation Error", breakdownError);
        return null;
      }
    }

    setIsSubmitting(true);
    try {
      const { clamped, anyClamped } = clampItemsForIntegrity(sovData.items);
      if (anyClamped) {
        setSovData((prev) => ({ ...prev, items: clamped }));
        showAlert(
          "warning",
          "Amounts Adjusted",
          "One or more items were below their previously billed amount and were floored to protect prior draws. Review the highlighted values, then save again.",
        );
        return null;
      }

      const totalInvoiced = calculateTotalInvoicedHelper(
        sovData.items,
        sovData.withHoldback,
        quickBooksTaxRates,
        companyProfile?.plugins?.quickbooks?.connected,
      );
      const dataToSubmit = {
        ...sovData,
        items: clamped,
        totalInvoiced,
        isCustomBreakdown,
        invoiceChangesSeperately,
      };

      const hasExistingInvoices =
        sovData.invoiceComponents?.length > 0 &&
        sovData.invoiceComponents.some(
          (ic) => ic.qbInvoiceId && ic.status !== "voided",
        );

      const saved = await saveSOV(dataToSubmit);
      if (!saved) return null;

      if (
        hasExistingInvoices &&
        companyProfile?.plugins?.quickbooks?.connected
      ) {
        const result = await generateQBInvoice(true);
        if (result?.success) {
          const resaved = await saveSOV({
            ...saved,
            items: result.items,
            invoiceComponents: result.invoiceComponents,
            generated: true,
            status: result.status,
            totalInvoiced,
            isCustomBreakdown,
            invoiceChangesSeperately,
          });
          if (resaved) return { sovData: resaved, regenerated: true };
        }
      }

      return { sovData: saved, regenerated: false };
    } catch (err) {
      showAlert("error", "Submit Error", `Failed to submit: ${err.message}`);
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Invoice component payment updates ────────────────────────────────
  const handleInvoiceComponentPaymentUpdate = (componentId, field, value) => {
    setSovData((prev) => ({
      ...prev,
      invoiceComponents: prev.invoiceComponents.map((ic) => {
        if (ic._id !== componentId) return ic;
        if (field === "status" && value === "paid") {
          return {
            ...ic,
            status: "paid",
            amountPaid: parseFloat(ic.total || ic.amount || 0).toFixed(2),
          };
        }
        if (field === "status" && value !== "paid")
          return { ...ic, status: value, amountPaid: "0.00" };
        if (field === "amountPaid") {
          const paid = parseFloat(value) || 0;
          const total = parseFloat(ic.total || ic.amount || 0);
          const autoStatus =
            paid <= 0 ? ic.status : paid >= total ? "paid" : ic.status;
          return { ...ic, amountPaid: value, status: autoStatus };
        }
        return { ...ic, [field]: value };
      }),
    }));
  };

  const saveInvoiceComponentPayment = async (componentId) => {
    try {
      const token = localStorage.getItem("token");
      const component = sovData.invoiceComponents.find(
        (ic) => ic._id === componentId,
      );
      if (!component) return;

      const response = await axios.put(
        `${BASE_URL}/api/sovs2/${sovData._id}/invoice-component/${componentId}/payment`,
        { amountPaid: component.amountPaid, status: component.status },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setSovData((prev) => ({
        ...prev,
        invoiceComponents: response.data.sov.invoiceComponents,
      }));
      setSovs((prev) =>
        prev.map((s) =>
          s._id === sovData._id
            ? { ...s, invoiceComponents: response.data.sov.invoiceComponents }
            : s,
        ),
      );
      syncStandaloneCache(sovData._id, {
        invoiceComponents: response.data.sov.invoiceComponents,
      });

      showAlert(
        "success",
        "Payment Updated",
        "Invoice payment updated successfully.",
      );
    } catch (err) {
      showAlert(
        "error",
        "Payment Update Error",
        `Failed to update payment: ${err.response?.data?.message || err.message}`,
      );
    }
  };

  // ── QuickBooks invoice generation ────────────────────────────────────
  const buildItemDescription = (item) => {
    let description = item.description || "";
    if (item.changeOrderNumber && item.referenceNumber) {
      const pricingSections =
        changeOrderPricingSections[item.changeOrderNumber];
      if (pricingSections) {
        const section = pricingSections.find(
          (ps) => ps.number === item.referenceNumber,
        );
        if (section?.title) {
          const prefix = `CO #${item.changeOrderNumber} - Section ${section.number}: ${section.title}`;
          if (description && !description.startsWith("CO #"))
            return `${prefix} - ${description}`;
          if (!description) return prefix;
        }
      }
      if (!description.startsWith("CO #"))
        return `CO #${item.changeOrderNumber} - ${description}`;
    }
    return description;
  };

  // Builds the components payload and runs ONE idempotent sync against
  // /invoicesV2. The server owns invoiceComponents: it matches our
  // componentKey values to what already exists in Mongo/QuickBooks and
  // updates in place (or creates / voids), so we never send "existing"
  // state and never create duplicates.
  const generateQBInvoice = async (saveAfter = false) => {
    if (isGeneratingInvoice) return { success: false };
    setIsGeneratingInvoice(true);

    const qbConnected = companyProfile?.plugins?.quickbooks?.connected;
    const getTaxRateForItem = (item) => {
      if (qbConnected) {
        const taxCode = quickBooksTaxRates.find(
          (tr) => tr.id === item.taxCodeId,
        );
        return parseFloat(taxCode?.taxRates?.[0]?.rate) || 0;
      }
      return parseFloat(item.taxRate) || 0;
    };

    try {
      const token = localStorage.getItem("token");
      const billingContact = project?.billingContact || {};
      const billingAccount = project?.accountId || {};

      const customer = {
        email: billingContact.email || "unknown@example.com",
        firstName: billingContact.firstName || "Unknown",
        lastName: billingContact.lastName || "Customer",
        companyName: billingAccount.companyName || "Unnamed Company",
        address: {
          street: billingAccount.address?.street || "",
          city: billingAccount.address?.city || "",
          state: billingAccount.address?.state || "",
          zip: billingAccount.address?.postalCode || "",
          country: billingAccount.address?.country || "",
        },
      };

      const lineItemsByComponent = {};
      const addToComponent = (
        componentKey,
        componentType,
        items,
        extra = {},
      ) => {
        if (!lineItemsByComponent[componentKey]) {
          lineItemsByComponent[componentKey] = {
            componentKey,
            component: componentType,
            docType: "invoice",
            lineItems: [],
            amount: "0.00",
            memo: "",
            changeOrderNumber: null,
            changeOrderId: null,
            ...extra,
          };
        }
        const comp = lineItemsByComponent[componentKey];
        items.forEach((item) => comp.lineItems.push(item));
        comp.amount = comp.lineItems
          .reduce((sum, item) => sum + parseFloat(item.amount || 0), 0)
          .toFixed(2);
      };

      const hasWork = (item) =>
        parseFloat(item.workCompletedThisPeriod || 0) !== 0;
      const toLine = (item, extra = {}) => ({
        qbItem: getDefaultQbItemId(),
        description: buildItemDescription(item),
        amount: parseFloat(item.workCompletedThisPeriod || 0),
        taxCodeId: item.taxCodeId,
        taxRate: getTaxRateForItem(item),
        ...extra,
      });

      // Negative change orders → one Credit Memo per CO, always separate,
      // always the full amount being credited this draw, no holdback.
      const creditItems = sovData.items.filter(
        (item) => isCreditMemoItem(item) && hasWork(item),
      );
      const creditGroups = {};
      creditItems.forEach((item) => {
        (creditGroups[item.changeOrderNumber] ||= []).push(item);
      });
      Object.entries(creditGroups).forEach(([coNum, items]) => {
        const key = `credit_memo_co_${coNum}`;
        const co = approvedChangeOrders.find(
          (c) => String(c.changeOrderNumber) === String(coNum),
        );
        addToComponent(
          key,
          "change_order",
          items.map((item) =>
            toLine(item, {
              changeOrderId: item.changeOrderId,
              referenceNumber: item.referenceNumber,
            }),
          ),
          {
            docType: "credit_memo",
            changeOrderNumber: coNum,
            changeOrderId: items[0].changeOrderId,
            memo: `Credit – ${co?.title || `Change Order #${coNum}`} (CO #${coNum})`,
            withHoldback: false,
          },
        );
      });

      // Everything else (credit rows are excluded — they never touch invoices)
      const invoiceItems = sovData.items.filter(
        (item) => !isCreditMemoItem(item) && hasWork(item),
      );

      if (invoiceChangesSeperately) {
        const ocItems = invoiceItems
          .filter((item) => !item.changeOrderNumber)
          .map((item) => toLine(item));
        if (ocItems.length > 0) {
          addToComponent("original_contract", "original_contract", ocItems, {
            memo: "Original Contract Work Completed This Period",
            withHoldback: sovData.withHoldback,
          });
        }

        const coGroups = {};
        invoiceItems
          .filter((item) => item.changeOrderNumber)
          .forEach((item) => {
            const groupKey = item.referenceNumber
              ? `ref_${item.referenceNumber}`
              : `co_${item.changeOrderNumber}`;
            if (!coGroups[groupKey]) {
              coGroups[groupKey] = {
                referenceNumber: item.referenceNumber || null,
                changeOrderNumbers: [],
                primaryChangeOrderId: item.changeOrderId,
                lineItems: [],
              };
            }
            coGroups[groupKey].lineItems.push(
              toLine(item, {
                changeOrderId: item.changeOrderId,
                referenceNumber: item.referenceNumber,
              }),
            );
            if (
              !coGroups[groupKey].changeOrderNumbers.includes(
                item.changeOrderNumber,
              )
            ) {
              coGroups[groupKey].changeOrderNumbers.push(
                item.changeOrderNumber,
              );
            }
          });

        Object.entries(coGroups).forEach(([groupKey, group]) => {
          const isRefGrouped = groupKey.startsWith("ref_");
          const primaryCoNum = group.changeOrderNumbers[0];
          const co = approvedChangeOrders.find(
            (c) => String(c.changeOrderNumber) === String(primaryCoNum),
          );
          addToComponent(groupKey, "change_order", group.lineItems, {
            changeOrderNumber: isRefGrouped
              ? group.referenceNumber
              : primaryCoNum,
            changeOrderId: group.primaryChangeOrderId,
            referenceNumber: group.referenceNumber || null,
            withHoldback: sovData.withHoldback,
            memo: isRefGrouped
              ? `Reference #${group.referenceNumber} (CO #${group.changeOrderNumbers.join(", #")})`
              : co?.title || `Change Order #${primaryCoNum}`,
          });
        });

        if (
          holdbackInvoiced &&
          parseFloat(sovData.holdbackInvoiceAmount || 0) > 0
        ) {
          const holdbackItem = quickBooksItems.find((i) =>
            (i.name || i.Name || "").toLowerCase().includes("holdback"),
          ) || { id: quickBooksItems[0]?.id || "" };
          addToComponent(
            "holdback_release",
            "holdback",
            [
              {
                qbItemId: holdbackItem.id,
                description: "Holdback Release",
                amount: parseFloat(sovData.holdbackInvoiceAmount),
                taxCodeId: "NON",
              },
            ],
            { memo: "Previous Holdback Release", withHoldback: false },
          );
        }
      } else {
        const allItems = invoiceItems.map((item) => toLine(item));
        if (allItems.length > 0) {
          addToComponent("combined", "original_contract", allItems, {
            memo: "Work Completed This Period",
            withHoldback: sovData.withHoldback,
          });
        }
      }

      if (Object.keys(lineItemsByComponent).length === 0) {
        showAlert(
          "error",
          "Nothing to Invoice",
          "No work has been entered for this draw, so there is nothing to send to QuickBooks.",
        );
        return { success: false };
      }

      const projectFullAddress =
        sovData.job || getAddressString(project.address) || "N/A";
      Object.values(lineItemsByComponent).forEach((comp) => {
        comp.projectAddress = projectFullAddress;
        comp.workCompletedFrom = sovData.workCompletedFrom || "";
        comp.workCompletedTo = sovData.workCompletedTo || "";
        comp.poNumber = sovData.poNumber || "";
        comp.invoiceDate = sovData.invoiceDate || "";
        comp.dueDate = sovData.dueDate || "";
      });

      const response = await axios.post(
        `${BASE_URL}/api/quickbooks/invoicesV2`,
        {
          customer,
          sovId: sovData._id,
          invoiceDate: sovData.invoiceDate,
          dueDate: sovData.dueDate,
          invoiceChangesSeperately,
          lineItemsByComponent,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const { invoiceComponents, status, message } = response.data;
      const updatedItems = sovData.items.map((i) => ({
        ...i,
        _addedAfterInvoice: false,
      }));

      setSovData((prev) => ({
        ...prev,
        invoiceComponents,
        generated: true,
        status: status || "generated",
        items: updatedItems,
      }));
      setSovs((prev) =>
        prev.map((s) =>
          s._id === sovData._id
            ? { ...s, invoiceComponents, status: status || s.status }
            : s,
        ),
      );
      syncStandaloneCache(sovData._id, {
        invoiceComponents,
        status: status || "generated",
      });

      if (!saveAfter) {
        showAlert(
          "success",
          "Success",
          message || "QuickBooks documents synced successfully!",
        );
      }

      return {
        success: true,
        invoiceComponents,
        status: status || "generated",
        items: updatedItems,
      };
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "An unexpected error occurred.";
      showAlert(
        "error",
        err.response?.status === 409
          ? "QuickBooks Sync Blocked"
          : "QuickBooks Sync Error",
        errorMessage,
      );
      return { success: false };
    } finally {
      setIsGeneratingInvoice(false);
    }
  };

  // First-time generation: create QB documents, then persist the draw items
  // (the server already saved the components) and return the fresh SOV.
  const generateQBInvoiceAndSave = async () => {
    const result = await generateQBInvoice(true);
    if (!result?.success) return null;
    const totalInvoiced = calculateTotalInvoicedHelper(
      sovData.items,
      sovData.withHoldback,
      quickBooksTaxRates,
      companyProfile?.plugins?.quickbooks?.connected,
    );
    return saveSOV({
      ...sovData,
      items: result.items,
      invoiceComponents: result.invoiceComponents,
      generated: true,
      status: result.status,
      totalInvoiced,
      isCustomBreakdown,
      invoiceChangesSeperately,
    });
  };

  // Void one invoice (QB void) or credit memo (QB delete — credit memos
  // can't be voided). The ERP row stays on the SOV as "voided".
  const voidInvoiceComponent = async (componentId) => {
    const comp = sovData.invoiceComponents?.find((c) => c._id === componentId);
    if (!comp) return;
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${BASE_URL}/api/quickbooks/documents/${sovData._id}/${componentId}/void`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const { invoiceComponents, status, generated } = response.data;
      setSovData((prev) => ({
        ...prev,
        invoiceComponents,
        status: status || prev.status,
        generated: generated ?? prev.generated,
      }));
      setSovs((prev) =>
        prev.map((s) =>
          s._id === sovData._id
            ? { ...s, invoiceComponents, status: status || s.status }
            : s,
        ),
      );
      syncStandaloneCache(sovData._id, {
        invoiceComponents,
        status: status || sovData.status,
      });
      showAlert(
        "success",
        "Success",
        comp.qbDocType === "credit_memo"
          ? "Credit memo deleted in QuickBooks."
          : "Invoice voided in QuickBooks.",
      );
    } catch (err) {
      showAlert(
        "error",
        "Void Error",
        err.response?.data?.message || err.message,
      );
    }
  };

  return {
    BASE_URL,
    // data
    sovData,
    setSovData,
    generateQBInvoiceAndSave,
    sovs,
    approvedChangeOrders,
    allChangeOrders,
    separateChangeOrders,
    standaloneSovsByCoId,
    companyProfile,
    quickBooksItems,
    quickBooksTaxRates,
    contacts,
    changeOrderPricingSections,
    // ui state
    isLoading,
    isGeneratingInvoice,
    isDownloading,
    setIsDownloading,
    showCreateForm,
    setShowCreateForm,
    editingSovId,
    isSubmitting,
    isCustomBreakdown,
    setIsCustomBreakdown,
    dueDateTouched,
    setDueDateTouched,
    taxRateData,
    showAddCoPicker,
    setShowAddCoPicker,
    customBreakdownItems,
    isBreakdownExpanded,
    setIsBreakdownExpanded,
    userEmail,
    showEmailForm,
    setShowEmailForm,
    pdfBuffer,
    setPdfBuffer,
    emailForm,
    setEmailForm,
    invoiceChangesSeperately,
    setInvoiceChangesSeperately,
    holdbackInvoiced,
    breakdownUnlocked,
    setBreakdownUnlocked,
    confirmUnlockIntent,
    setConfirmUnlockIntent,
    loadingStandaloneId,
    standaloneHoldbackStats,
    isStandaloneMode,
    alert,
    showAlert,
    closeAlert,
    contactOptions,
    isBreakdownLocked,
    eligibleCOsToAdd,
    getDefaultQbItemId,
    // handlers
    handleSOVChange,
    handlePercentageBlur,
    handleItemChange,
    removeLineItem,
    moveItemUp,
    moveItemDown,
    breakdownItemHasProgress,
    handleBreakdownItemChange,
    disableCustomBreakdown,
    addBreakdownItem,
    removeBreakdownItem,
    addChangeOrderToDraw,
    handleViewSOV,
    handleOpenStandaloneSov,
    handleCreateSOV,
    handleInvoiceHoldbackSOV,
    calculateOutstandingHoldback,
    saveSOV,
    handleSubmit,
    handleInvoiceComponentPaymentUpdate,
    saveInvoiceComponentPayment,
    buildItemDescription,
    generateQBInvoice,
    voidInvoiceComponent,
    prepareHoldbackReleaseForCurrentSov,
  };
}
